import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import {
  ApiCallHandler,
  EoHttpClient,
  IHttpParams,
  STATUS,
  PermissionService,
} from 'ngx-eo-web-components';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CoreService } from '../../modules/core/core.service';
import { IAuthConfirmBindingModel } from '../../modules/core/models/auth-confirm-bind.model';
import { ChangePassModel } from '../../modules/core/models/change-pass.model';
import {
  IToken,
  LoginResponse,
} from '../../modules/core/models/token-response.model';
import { IUserResetPasswordPayload } from '../../modules/core/models/user-reset-password.model';
import { User } from '../../modules/core/models/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public userResetPasswordCallHandler = new ApiCallHandler<User | null>();
  public userEmailConfirmCallHandler = new ApiCallHandler<User | null>();

  gsis2Token$ = new BehaviorSubject<string | null>(null);

  public userDataCallHandler = new ApiCallHandler<any | null>();
  // public decodedToken = new BehaviorSubject<IToken | null>(null);
  public roles$ = new BehaviorSubject<string | null>(null);
  public roleLiterals$ = new BehaviorSubject<string | null>(null);
  public RegisterSuccess$ = false; //new BehaviorSubject<boolean | null>(false);
  public tokenError$ = new BehaviorSubject<string | null>(null);
  public tokenStatus$ = new BehaviorSubject<STATUS>(STATUS.pending);
  public token$ = new BehaviorSubject<IToken | null>(null);
  public checkAFMStatus$ = new Subject<string>();
  public loginStatus$ = new BehaviorSubject<STATUS>(STATUS.pending);
  public loginSuccess$ = new BehaviorSubject<any>(null);
  public loginError$ = new BehaviorSubject<any>(null);
  public saveRegister$ = new Subject<string>();

  constructor(
    private http: EoHttpClient,
    private coreService: CoreService,
    private router: Router,
    private permissionService: PermissionService
  ) { }

  hasRoles(userRoles: string[]): boolean {
    const token = this.token$.getValue();
    if (!token || !token.role) {
      return false;
    }

    // Handle both array and string formats for role
    let tokenRoles: string[] = [];
    if (Array.isArray(token.role)) {
      tokenRoles = token.role;
    } else if (typeof token.role === 'string') {
      tokenRoles = token.role.includes(',') 
        ? token.role.split(',').map(r => r.trim())
        : [token.role];
    }

    return userRoles.some((role) => {
      // // Handle negation (e.g., '!ClinicAdmin')
      // if (role.startsWith('!')) {
      //   const strippedRole = role.substring(1).trim();
      //   return !tokenRoles.includes(strippedRole);
      // }

      return tokenRoles.includes(role);
    });
  }

  //for multiple roles
  // return userRoles.some(role => {
  //   if (role.startsWith('!')) {
  //     const strippedRole = role.substring(1).trim();
  //     return !token.roles?.includes(strippedRole);
  //   }
  //   return token.roles?.includes(role);
  // })

  // public HospitalID: string = this.GetLocalStorageValue('HospitalID') ?? '-1';

  fetchToken(username: string, password: string) {
    // let headers = new HttpHeaders();
    // headers = headers.append(
    //   'Content-Type',
    //   'application/x-www-form-urlencoded'
    // );

    // let body = new URLSearchParams();
    // body.set('grant_type', 'password');
    // body.set('username', username);
    // body.set('password', password);

    const body = {
      username: username,
      password: password,
      edapySpecialtyId: -1,
    };

    const params: IHttpParams = {
      url: `${environment.apiUrl}/v1/Auth/login`,
      body: body,
      status$: this.loginStatus$,
      success$: this.loginSuccess$,
      error$: this.loginError$,
    };

    //this.loginStatus$.next('loading');//STATUS.loading);
    // this.tokenStatus$.next(STATUS.loading);
    this.http.post<LoginResponse>(params).subscribe({
      next: (success) => {
        // const issued = new Date().getTime();
        // const expires = issued + success.expires_in * 1000;
        // success.issued = issued;
        // success.expires = expires;
        //this.loginStatus$.next('success');//STATUS.success);
        this.setToken(success?.token);
      },
      error: (error) => {
        this.loginStatus$.next(STATUS.error); //STATUS.error);
        this.tokenStatus$.next(STATUS.error);
        this.tokenError$.next(error.message);
      },
    });
  }

  resetPassword(payload: IUserResetPasswordPayload) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Auth/reset-password',
      this.userResetPasswordCallHandler,
      payload
    );
    return this.http.post<User>(param);
  }

  confirmEmail(payload: IAuthConfirmBindingModel) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Auth/confirm',
      this.userEmailConfirmCallHandler,
      payload
    );
    return this.http.post<User>(param);
  }

  requestResetPassword(userId: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/Auth/request-reset-password?userId=${userId}`,
      this.userResetPasswordCallHandler
    );
    return this.http.get<User>(param);
  }

  requestConfirmEmail(userId: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/Auth/request-confirm-email/${userId}`,
      this.userEmailConfirmCallHandler
    );
    return this.http.get<User>(param);
  }

  showPwdModal$ = new BehaviorSubject<boolean>(false);
  pwdDaysLeft$ = new BehaviorSubject<number>(-1);
  changePwdNext$ = new BehaviorSubject<boolean>(false);

  setToken(token: string) {
    localStorage.setItem('token', JSON.stringify(token));

    const decoded: any = jwtDecode(token);

    const show = decoded.ShowPasswordExpirationModal === 'true';
    const days = +decoded.PasswordExpirationDays;
    const changeNext = decoded.ChangePasswordOnNextUserLogin === 'true';
    this.pwdDaysLeft$.next(isNaN(days) ? -1 : days);
    this.changePwdNext$.next(changeNext);
    this.showPwdModal$.next(show);

    this.token$.next(decoded as IToken);
    this.tokenStatus$.next(STATUS.success);
  }

  logout() {
    localStorage.clear();
    this.token$.next(null);
    this.tokenError$.next(null);

    this.showPwdModal$.next(false);
    this.pwdDaysLeft$.next(-1);
    this.changePwdNext$.next(false);

    this.clearLastWarnDays();
    this.router.navigate(['login']);
  }

  hydrate() {
    const token = localStorage.getItem('token');
    if (!token) return;

    const decoded: any = jwtDecode(token);
    this.token$.next(decoded as IToken);

    const show = decoded.ShowPasswordExpirationModal === 'true';
    const days = +decoded.PasswordExpirationDays;
    const changeNext = decoded.ChangePasswordOnNextUserLogin === 'true';
    this.pwdDaysLeft$.next(isNaN(days) ? -1 : days);
    this.changePwdNext$.next(changeNext);
    this.showPwdModal$.next(show);
  }

  public forgotPasswordCh = new ApiCallHandler<void>();

  forgotPassword(payload: { email: string }): Observable<void> {
    const params: IHttpParams = this.coreService.initCall(
      '/v1/Auth/forgot-password',
      this.forgotPasswordCh,
      payload
    );

    return this.http.post<void>(params);
  }

  public changePasswordCh = new ApiCallHandler<null>();
  changePassword(payload: ChangePassModel) {
    const params = this.coreService.initCall(
      '/v1/Auth/changepassword',
      this.changePasswordCh,
      payload
    );
    return this.http.post<void>(params);
  }

  private readonly warnKey = 'lastPwdWarnDays';

  getLastWarnDays(): number {
    const val = localStorage.getItem(this.warnKey);
    return val ? +val : Number.NaN;
  }

  setLastWarnDays(days: number) {
    localStorage.setItem(this.warnKey, String(days));
  }

  clearLastWarnDays() {
    localStorage.removeItem(this.warnKey);
  }

  // TokenExpirationCheck() {
  //   let token = localStorage.getItem('token');
  //   if (!token) {
  //     this.logout();
  //     return;
  //   }
  //   let decodedToken = jwtDecode<IToken>(token);
  //   if (new Date(decodedToken.exp * 100) < new Date()) this.logout();
  // }
}
